import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faLock,
  faFilter,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import './Wedo20.css';

const Wedo20 = () => {
  const [projects, setProjects] = useState([]);
  const [visibleProjects, setVisibleProjects] = useState(8);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    difficulty: '',
    type: '',
    isFree: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/allwedoprojects`);
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        const data = await response.json();
 

        
        setProjects(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = searchTerm === '' || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDifficulty = !filters.difficulty || project.difficulty === filters.difficulty;
    const matchesType = !filters.type || project.type === filters.type;
    const matchesPrice = filters.isFree === null || project.isFree === filters.isFree;
    
    return matchesSearch && matchesDifficulty && matchesType && matchesPrice;
  });

  const handleImageError = (e) => {
    e.target.src = '/assets/img/default-wedo.jpg';
    e.target.alt = 'Image not available';
  };

  const handleContactClick = (project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  // Close the popup with the Escape key
  useEffect(() => {
    if (!selectedProject) return undefined;
    const onKey = (e) => e.key === 'Escape' && setSelectedProject(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedProject]);

  const handleWhatsAppContact = () => {
    if (selectedProject) {
      const phoneNumber = process.env.REACT_APP_MOBILE_NUMBER1; // same number as the rest of the site
      const message = selectedProject.isFree
        ? `Hello! Can you send me the free "${selectedProject.title}" WeDo 2.0 building instructions?`
        : `Hello! I'm interested in getting access to the "${selectedProject.title}" WeDo 2.0 building instructions.`;
      const whatsappUrl = `https://api.whatsapp.com/send/?phone=${phoneNumber}&text=${encodeURIComponent(
        message
      )}&type=phone_number&app_absent=0`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
    closeModal();
  };

  const resetFilters = () => {
    setFilters({
      difficulty: '',
      type: '',
      isFree: null
    });
    setSearchTerm('');
  };

  const loadMoreProjects = () => {
    setVisibleProjects(prev => prev + 8);
  };

  // Get unique values for filters
  const difficulties = [...new Set(projects.map(p => p.difficulty))].filter(Boolean);
  const types = [...new Set(projects.map(p => p.type))].filter(Boolean);

  if (loading) {
    return (
      <div className="wedo-container">
        <div className="wedo-loading">
          <div className="wedo-spinner"></div>
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wedo-container">
        <div className="wedo-error">
          <h3>Projects couldn’t load right now</h3>
          <p>Please try again in a moment.</p>
          <button onClick={() => window.location.reload()} className="wedo-btn wedo-btn-primary">Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="wedo-container">
      <div className="wedo-inner-container">
        {/* Header */}
        <div className="wedo-header wedo-text-center wedo-mb-4">
          <span className="wedo-eyebrow">LEGO® Education WeDo 2.0</span>
          <h1 className="wedo-section-title">WeDo 2.0 <span>building instructions</span></h1>
          <p className="wedo-section-subtitle">
            Step-by-step builds for classrooms, clubs and home. Free projects and premium ones, sent to you on WhatsApp.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="wedo-controls wedo-mb-4">
          <div className="wedo-search-container">
            <div className="wedo-search-input-group">
              <FontAwesomeIcon icon={faSearch} className="wedo-search-icon" />
              <input
                type="search"
                aria-label="Search projects"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="wedo-search-input"
              />
              <button 
                type="button"
                className="wedo-filter-toggle-btn"
                aria-expanded={showFilters}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>Filter</span>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="wedo-filters-panel">
              <div className="wedo-filter-row">
                <div className="wedo-filter-group">
                  <label>Difficulty:</label>
                  <select
                    value={filters.difficulty}
                    onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
                    className="wedo-filter-select"
                  >
                    <option value="">All Levels</option>
                    {difficulties.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div className="wedo-filter-group">
                  <label>Category:</label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({...filters, type: e.target.value})}
                    className="wedo-filter-select"
                  >
                    <option value="">All Categories</option>
                    {types.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="wedo-filter-group">
                  <label>Access:</label>
                  <select
                    value={filters.isFree === null ? '' : filters.isFree}
                    onChange={(e) => setFilters({...filters, isFree: e.target.value === '' ? null : e.target.value === 'true'})}
                    className="wedo-filter-select"
                  >
                    <option value="">All Access</option>
                    <option value="true">Free</option>
                    <option value="false">Premium</option>
                  </select>
                </div>

                <button 
                  className="wedo-reset-filters-btn"
                  onClick={resetFilters}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="wedo-results-count wedo-mb-3">
          <span>{filteredProjects.length} projects found</span>
        </div>

        {/* Projects Grid */}
        <div className="wedo-projects-grid">
          {filteredProjects.length === 0 ? (
            <div className="wedo-no-projects wedo-text-center wedo-py-5">
              <h3>No projects found</h3>
              <p>Try adjusting your search or filters</p>
              <button onClick={resetFilters} className="wedo-btn wedo-btn-primary wedo-mt-2">
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <div className="wedo-projects-grid-inner">
                {filteredProjects.slice(0, visibleProjects).map((project) => (
                  <div key={project.id} className="wedo-project-card">
                    <div className="wedo-project-image-container">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="wedo-project-image"
                        onError={handleImageError}
                        loading="lazy"
                      />
                      {!project.isFree && (
                        <div className="wedo-premium-tag">
                          <FontAwesomeIcon icon={faLock} />
                          <span>Premium</span>
                        </div>
                      )}
                      <div className={`wedo-difficulty-tag wedo-difficulty-${project.difficulty?.toLowerCase()}`}>
                        {project.difficulty}
                      </div>
                    </div>

                    <div className="wedo-project-content">
                      <h3 className="wedo-project-title">{project.title}</h3>
                      
                      <div className="wedo-project-details">
                        <div className="wedo-detail-item">
                          <span className="wedo-detail-label">Category:</span>
                          <span className="wedo-detail-value">{project.type}</span>
                        </div>
                        {project.slides && (
                          <div className="wedo-detail-item">
                            <span className="wedo-detail-label">Steps:</span>
                            <span className="wedo-detail-value">{project.slides}</span>
                          </div>
                        )}
                      </div>

                      <button 
                        className={`wedo-project-action-btn ${project.isFree ? 'wedo-free-btn' : 'wedo-premium-btn'}`}
                        onClick={() => handleContactClick(project)}
                      >
                        {project.isFree ? 'Get it free' : 'Request access'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {visibleProjects < filteredProjects.length && (
                <div className="wedo-text-center wedo-mt-4">
                  <button 
                    onClick={loadMoreProjects}
                    className="wedo-btn wedo-btn-primary wedo-load-more-btn"
                  >
                    Load More ({filteredProjects.length - visibleProjects} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Contact Modal */}
      {selectedProject && (
        <div className="wedo-contact-modal-overlay" onClick={closeModal}>
          <div className="wedo-contact-modal" role="dialog" aria-modal="true" aria-label="Request access" onClick={(e) => e.stopPropagation()}>
            <div className="wedo-modal-header">
              <h4>{selectedProject.isFree ? 'Get this project' : 'Request access'}</h4>
              <button type="button" className="wedo-close-modal-btn" onClick={closeModal} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="wedo-modal-content">
              <div className="wedo-modal-project-preview">
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title}
                  className="wedo-modal-image"
                  onError={handleImageError}
                />
                <div className="wedo-modal-project-info">
                  <h5>{selectedProject.title}</h5>
                  <div className="wedo-modal-details">
                    <span className="wedo-modal-category">{selectedProject.type}</span>
                    <span className="wedo-modal-difficulty">{selectedProject.difficulty}</span>
                    {selectedProject.slides && (
                      <span className="wedo-modal-steps">{selectedProject.slides} steps</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="wedo-modal-message">
                <p>
                  We’ll send the building instructions for <strong>"{selectedProject.title}"</strong> on WhatsApp.
                </p>
              </div>
              
              <div className="wedo-modal-actions">
                <button className="wedo-btn wedo-btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button className="wedo-btn wedo-btn-primary" onClick={handleWhatsAppContact}>
                  Continue on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wedo20;